import { useState } from 'react'
import {X, Star, AlertCircle, Clock} from 'lucide-react'
import { EVALUATION_ASPECTS, computeAverage, type EvaluationScores } from '@/lib/evaluationAspects'
import type { WorkLink } from '@/hooks/useLinks'

interface EvalAspect {
  key: string
  label: string
  description?: string
}

interface EvaluationFormModalProps {
  link: WorkLink
  /** Aspectos a evaluar (configurados por la empresa). Si no se pasan, se usan los base. */
  aspects?: EvalAspect[]
  /** Evaluaciones ya existentes para este colaborador (empresa→colaborador) */
  existingEvaluations?: Array<{ period: string; collaboratorUserId: string; linkId?: string }>
  onClose: () => void
  onSubmit: (payload: {
    period: string
    scores: EvaluationScores
    average: number
    comments: string
    observations: string
    recommendations: string
  }) => Promise<void>
}

function defaultPeriod(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Fecha mínima permitida: mes en que inició la vinculación */
function minPeriod(link: WorkLink): string {
  const ref = link.startDate || link.createdAt
  if (!ref) return '2020-01'
  return ref.slice(0, 7)
}

/**
 * Para vínculos finalizados, verifica ventana de 24 h desde la finalización.
 */
function withinGraceWindow(link: WorkLink): boolean {
  if (link.status !== 'finalizada') return true
  const ref = link.endDate || link.updatedAt
  if (!ref) return false
  return Date.now() - new Date(ref).getTime() <= 24 * 60 * 60 * 1000
}

function EvaluationFormModal({ link, aspects, existingEvaluations = [], onClose, onSubmit }: EvaluationFormModalProps) {
  const evalAspects: EvalAspect[] = aspects && aspects.length > 0 ? aspects : EVALUATION_ASPECTS
  const currentPeriod = defaultPeriod()
  const [period, setPeriod] = useState(currentPeriod)
  const [scores, setScores] = useState<EvaluationScores>(
    evalAspects.reduce((acc, a) => ({ ...acc, [a.key]: 8 }), {} as EvaluationScores)
  )
  const [comments, setComments] = useState('')
  const [observations, setObservations] = useState('')
  const [recommendations, setRecommendations] = useState('')
  const [saving, setSaving] = useState(false)

  const average = computeAverage(scores)

  // ── Validaciones ─────────────────────────────────────────────────────────
  const alreadyEvaluatedThisPeriod = existingEvaluations.some(
    (e) => e.period === period && e.collaboratorUserId === link.collaboratorUserId
  )

  const min = minPeriod(link)
  const periodTooEarly = period < min

  const graceOk = withinGraceWindow(link)
  const canSave = !alreadyEvaluatedThisPeriod && !periodTooEarly && graceOk

  const setScore = (key: string, value: number) => {
    setScores((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      await onSubmit({ period, scores, average, comments, observations, recommendations })
    } finally {
      setSaving(false)
    }
  }

  const avgColor = average >= 8 ? 'text-emerald-600' : average >= 6 ? 'text-amber-600' : 'text-red-500'

  // ── Pantalla de bloqueo (fuera de ventana 24h) ────────────────────────────
  if (!graceOk && link.status === 'finalizada') {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
        <div className="my-8 w-full max-w-md rounded-2xl bg-white shadow-xl p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 mx-auto mb-4">
            <Clock size={28} className="text-slate-500" />
          </div>
          <p className="text-lg font-bold text-slate-900">Plazo expirado</p>
          <p className="mt-2 text-sm text-slate-500">
            Una vez finalizada la vinculación, ambas partes tienen <strong>24 horas</strong> para
            completar la evaluación pendiente. El plazo para esta vinculación ya venció.
          </p>
          <button
            onClick={onClose}
            className="mt-6 rounded-lg bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Cerrar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Evaluación mensual</h2>
            <p className="text-sm text-slate-500">{link.collaboratorName || link.collaboratorEmail}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={20} />
          </button>
        </div>

        {/* Aviso vínculo finalizado dentro de ventana */}
        {link.status === 'finalizada' && (
          <div className="mx-6 mt-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
            <Clock size={15} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              La vinculación con este colaborador ha finalizado. Tienes <strong>24 horas</strong> desde
              la desvinculación para completar la evaluación del mes en curso.
            </p>
          </div>
        )}

        <div className="space-y-5 px-6 py-5">
          {/* Periodo + promedio */}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Periodo evaluado</label>
              <input
                type="month"
                value={period}
                min={min}
                max={currentPeriod}
                onChange={(e) => setPeriod(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
              {periodTooEarly && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
                  <AlertCircle size={12} />
                  El periodo no puede ser anterior al inicio de la vinculación ({min}).
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-2">
              <Star size={18} className={avgColor} />
              <span className="text-sm text-slate-500">Promedio</span>
              <span className={`text-2xl font-bold ${avgColor}`}>{average.toFixed(1)}</span>
            </div>
          </div>

          {/* Alerta periodo ya evaluado */}
          {alreadyEvaluatedThisPeriod && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                Ya existe una evaluación para <strong>{link.collaboratorName || 'este colaborador'}</strong> en
                el periodo <strong>{period}</strong>. Solo se permite <strong>una evaluación por mes</strong>.
                Cambia el periodo o cancela.
              </p>
            </div>
          )}

          {/* Aspectos */}
          <div className="space-y-4">
            {evalAspects.map((aspect) => (
              <div key={aspect.key}>
                <div className="mb-1 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{aspect.label}</p>
                    <p className="text-xs text-slate-400">{aspect.description}</p>
                  </div>
                  <span className="ml-3 w-9 shrink-0 rounded-md bg-slate-900 py-1 text-center text-sm font-bold text-amber-400">
                    {scores[aspect.key]}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={scores[aspect.key]}
                  onChange={(e) => setScore(aspect.key, Number(e.target.value))}
                  className="w-full accent-amber-500"
                />
              </div>
            ))}
          </div>

          {/* Textos */}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Comentarios</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
                placeholder="Aspectos generales del desempeño..."
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Observaciones</label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
                placeholder="Situaciones puntuales a destacar..."
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Recomendaciones</label>
              <textarea
                value={recommendations}
                onChange={(e) => setRecommendations(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
                placeholder="Sugerencias de mejora o desarrollo..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !canSave}
            title={
              alreadyEvaluatedThisPeriod
                ? 'Ya existe una evaluación para este periodo'
                : periodTooEarly
                ? 'El periodo no puede ser anterior a la vinculación'
                : undefined
            }
            className="rounded-lg bg-amber-400 px-5 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? 'Guardando...' : 'Guardar evaluación'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EvaluationFormModal
